import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { differenceInMinutes } from "date-fns";
import { calculateBookingPrice, type PricingRule, type SpecialPriceDate } from "@/lib/pricing";
import type { BookingSheetFormProps } from "./types";


interface BookingSheetSubscriptionFormProps extends BookingSheetFormProps {
  paymentMethods: { value: string; label: string }[];
  customers: any[];
  selectedCustomerId: string | null;
  onSelectCustomer: (id: string | null, name?: string) => void;
  customerSearch: string;
  onCustomerSearch: (v: string) => void;
  organizationId: string | null;
  pricingRules: PricingRule[];
  specialDates: SpecialPriceDate[];
  hallId: string | null;
  startTime: Date | null;
  endTime: Date | null;
}

export default function BookingSheetSubscriptionForm({
  formState,
  onFieldChange,
  paymentMethods: _paymentMethods,
  customers,
  selectedCustomerId,
  onSelectCustomer,
  customerSearch,
  onCustomerSearch,
  organizationId,
  pricingRules,
  specialDates,
  hallId,
  startTime,
  endTime,
}: BookingSheetSubscriptionFormProps) {
  const { t } = useTranslation("admin");
  // PR-AY2b (mechanical apply of PR-AY2a Tables<> pattern): per-site cast
  // narrows formState (Record<string, unknown> in shared base type).
  const {
    subCustomerSearch,
    subFrom,
    subTo,
    subDays,
    subInterval,
    subPrice,
    subComment,
    paymentMethod,
  } = formState as {
    subCustomerSearch?: string;
    subFrom?: string;
    subTo?: string;
    subDays: number[];
    subInterval?: number;
    subPrice?: number | string;
    subComment?: string;
    paymentMethod?: string;
  };

  const DAY_NAMES = [t("bookingSheet.daySun"), t("bookingSheet.dayMon"), t("bookingSheet.dayTue"), t("bookingSheet.dayWed"), t("bookingSheet.dayThu"), t("bookingSheet.dayFri"), t("bookingSheet.daySat")];

  // Inline create-customer mini-form
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [creating, setCreating] = useState(false);

  const selectedCustomer = customers.find((c: any) => c.id === selectedCustomerId);

  // Auto-calculated price per occasion
  const autoPrice = useMemo(() => {
    if (!hallId || !startTime || !endTime || pricingRules.length === 0) return null;
    const durationMin = differenceInMinutes(endTime, startTime);
    if (durationMin <= 0) return null;
    const result = calculateBookingPrice(pricingRules, hallId, startTime, durationMin, "standard", specialDates);
    return result.total > 0 ? result.total : null;
  }, [hallId, startTime, endTime, pricingRules, specialDates]);

  // Pre-fill price when autoPrice changes and no manual price set
  const currentSubPrice = subPrice as string;
  useMemo(() => {
    if (autoPrice != null && !currentSubPrice) {
      onFieldChange("subPrice", String(autoPrice));
    }
    // Only run when autoPrice changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPrice]);

  const handleCreateCustomer = async () => {
    if (!newName.trim() || !organizationId) return;
    setCreating(true);
    try {
      const { data, error } = await supabase
        .from("customers")
        .insert({
          organization_id: organizationId,
          full_name: newName.trim(),
          email: newEmail.trim() || null,
          phone: newPhone.trim() || null,
        })
        .select()
        .single();
      if (error) throw error;
      toast.success(t("bookingSheet.customerCreated"));
      onSelectCustomer(data.id, data.full_name || data.email);
      setShowCreateCustomer(false);
      setNewName("");
      setNewEmail("");
      setNewPhone("");
    } catch (err: any) {
      toast.error(err.message || t("bookingSheet.couldNotCreateCustomer"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-3 border-t border-border pt-3">
      {/* Customer search */}
      <div>
        <Label className="text-xs">{t("bookingSheet.customer")}</Label>
        <Input
          placeholder={t("bookingSheet.searchCustomer")}
          value={subCustomerSearch}
          onChange={(e) => { onCustomerSearch(e.target.value); onSelectCustomer(null); }}
          className="h-9 text-sm"
        />
        {customers.length > 0 && !selectedCustomerId && customerSearch && (
          <div className="border rounded-md max-h-40 overflow-y-auto mt-1 shadow-sm">
            {customers.map((c: any) => (
              <button
                key={c.id}
                className="w-full text-left px-3 py-2 text-xs hover:bg-accent flex flex-col gap-0.5 border-b last:border-0"
                onClick={() => { onSelectCustomer(c.id, c.full_name || c.email); setShowCreateCustomer(false); }}
              >
                <span className="font-medium">{c.full_name || t("bookingSheet.unnamed")}</span>
                <span className="text-muted-foreground">
                  {c.email}{c.phone ? ` · ${c.phone}` : ""}
                </span>
              </button>
            ))}
          </div>
        )}
        {customers.length === 0 && customerSearch.length >= 2 && !selectedCustomerId && (
          <div className="mt-1">
            {!showCreateCustomer ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-xs h-7"
                onClick={() => { setShowCreateCustomer(true); setNewName(customerSearch); }}
              >
                + {t("bookingSheet.createNewCustomer")}
              </Button>
            ) : (
              <div className="border rounded-md p-3 space-y-2 bg-muted/30">
                <p className="text-xs font-medium">{t("bookingSheet.newCustomer")}</p>
                <Input
                  placeholder={t("bookingSheet.nameRequired")}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="h-8 text-sm"
                  autoFocus
                />
                <Input
                  placeholder={t("bookingSheet.email")}
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="h-8 text-sm"
                  type="email"
                />
                <Input
                  placeholder={t("bookingSheet.phone")}
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  className="h-8 text-sm"
                  type="tel"
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={!newName.trim() || creating}
                    onClick={handleCreateCustomer}
                  >
                    {creating ? t("bookingSheet.creating") : t("bookingSheet.create")}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setShowCreateCustomer(false)}
                  >
                    {t("bookingSheet.cancel")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        {selectedCustomer && (
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="secondary" className="text-xs">{selectedCustomer.full_name || selectedCustomer.email}</Badge>
            <button onClick={() => { onSelectCustomer(null); onCustomerSearch(""); }} className="text-xs text-muted-foreground hover:text-foreground">×</button>
          </div>
        )}
        <p className="text-[10px] text-muted-foreground mt-0.5">{t("bookingSheet.leaveEmptyWalkIn")}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">{t("bookingSheet.startDate")}</Label>
          <Input type="date" value={subFrom} onChange={(e) => onFieldChange("subFrom", e.target.value)} className="h-9 text-sm" />
        </div>
        <div>
          <Label className="text-xs">{t("bookingSheet.endDate")}</Label>
          <Input type="date" value={subTo} onChange={(e) => onFieldChange("subTo", e.target.value)} className="h-9 text-sm" />
        </div>
      </div>

      <div>
        <Label className="text-xs">{t("bookingSheet.weekdays")}</Label>
        <div className="flex gap-1.5 mt-1">
          {DAY_NAMES.map((name, i) => (
            <button
              key={i}
              onClick={() => onFieldChange("subDays", subDays.includes(i) ? subDays.filter((d: number) => d !== i) : [...subDays, i])}
              className={cn(
                "w-8 h-8 rounded-md text-xs font-medium border transition-colors",
                subDays.includes(i)
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-muted-foreground border-border hover:bg-accent"
              )}
            >
              {name}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-xs">{t("bookingSheet.repeat")}</Label>
        <Select value={subInterval} onValueChange={(v) => onFieldChange("subInterval", v)}>
          <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">{t("bookingSheet.everyWeek")}</SelectItem>
            <SelectItem value="2">{t("bookingSheet.everyOtherWeek")}</SelectItem>
            <SelectItem value="3">{t("bookingSheet.every3rdWeek")}</SelectItem>
            <SelectItem value="4">{t("bookingSheet.every4thWeek")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Price with auto-calculation */}
      <div>
        <Label className="text-xs">{t("bookingSheet.pricePerOccasion")}</Label>
        <Input
          value={subPrice}
          onChange={(e) => onFieldChange("subPrice", e.target.value)}
          className="h-9 text-sm"          placeholder={autoPrice != null ? String(autoPrice) : "0"}
        />
        {autoPrice != null && (
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {t("bookingSheet.calculatedPricePerOccasion", { price: autoPrice })}
          </p>
        )}
      </div>

      <div>
        <Label className="text-xs">{t("bookingSheet.payment")}</Label>
        <Select value={paymentMethod} onValueChange={(v) => onFieldChange("paymentMethod", v)}>
          <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="customer_pays">{t("bookingSheet.customerPays")}</SelectItem>
            <SelectItem value="paid_cash_swish">{t("bookingSheet.paidCashSwish")}</SelectItem>
            <SelectItem value="invoice">{t("bookingSheet.invoice")}</SelectItem>
            <SelectItem value="credits">{t("bookingSheet.punchCard")}</SelectItem>
            <SelectItem value="free">{t("bookingSheet.freeInternal")}</SelectItem>
            <SelectItem value="unpaid">{t("bookingSheet.unpaid")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs">{t("bookingSheet.comment")}</Label>
        <Textarea
          value={(subComment as string) ?? ""}
          onChange={(e) => onFieldChange("subComment", e.target.value)}
          className="text-sm"
          rows={2}
          placeholder={t("bookingSheet.subscriptionNotePlaceholder")}
        />
      </div>
    </div>
  );
}
